// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useMemo } from 'react';
import { GridColDef, GridValidRowModel } from '@mui/x-data-grid';
import { exportToCSV } from '../../utils/csvExport';

interface UseCsvExportOptions {
  filename: string;
}

interface UseCsvExportResult {
  handleExport: () => void;
  isExportDisabled: boolean;
}

/**
 * React hook for CSV export functionality.
 * Provides a callback to trigger export and a disabled state indicator.
 */
export const useCsvExport = <T extends GridValidRowModel>(
  columns: GridColDef<T>[],
  rows: T[] | undefined,
  options: UseCsvExportOptions,
): UseCsvExportResult => {
  const isExportDisabled = useMemo(() => !rows || rows.length === 0, [rows]);

  const handleExport = useCallback(() => {
    if (!rows || rows.length === 0) {
      return;
    }

    exportToCSV(columns, rows, { filename: options.filename });
  }, [columns, rows, options.filename]);

  return {
    handleExport,
    isExportDisabled,
  };
};
